"use client"

import { useState, useRef } from "react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Building2, Check, Loader2, X } from "lucide-react"
import { useAuth } from "@/hooks/use-auth"
import { useImpersonation } from "@/hooks/use-impersonation"
import { useTenantsQuery } from "@/hooks/queries/tenants"
import { useTranslations } from "next-intl"
import { isSelectableTenant } from "@/lib/auth/selectable-tenant"


export function TenantSwitcher() {
  const tUi = useTranslations("Shell.TenantSwitcher")

  const { user } = useAuth()
  const { switching, impersonate, stopImpersonation } = useImpersonation()
  const [filter, setFilter] = useState("")
  const filterInputRef = useRef<HTMLInputElement>(null)

  // Get current tenant ID (either impersonated or user's own)
  const currentTenantId = user?.impersonated_tenant_id || user?.tenant_id

  // Cross-tenant roles (super_user, integrator) can switch tenants.
  const canSwitch = !!user && (user.isSuperUser || user.isIntegrator)
  const { data, isLoading: loading } = useTenantsQuery({ enabled: canSwitch })
  // Cosmetic list trim, NOT a security boundary: this quick-switch dropdown
  // hides inactive/archived tenants to avoid clutter. Impersonation is not
  // server-gated on tenant status, and the tenant-management admin table can
  // still switch to inactive tenants on purpose (only archived is off-limits).
  // The /api/tenants endpoint is shared with admin/billing views that need the
  // full list, so filter here rather than at the route.
  const tenants = (data ?? []).filter(isSelectableTenant)

  const currentTenant = tenants.find((t) => t.id === currentTenantId)
  const isImpersonating = !!user?.impersonated_tenant_id

  const normalizedFilter = filter.trim().toLowerCase()
  const filteredTenants = normalizedFilter
    ? tenants.filter((t) => t.name.toLowerCase().includes(normalizedFilter))
    : tenants

  if (!canSwitch) {
    return null
  }

  return (
    <div className="flex min-w-0 items-center gap-1">
      <DropdownMenu onOpenChange={(open) => !open && setFilter("")}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="min-w-0 justify-start"
            disabled={switching}
          >
            {switching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Building2 className="h-4 w-4 mr-2" />}
            <span className="truncate">{currentTenant?.name || tUi("selectTenant")}</span>
            {isImpersonating && (
              <span className="ml-2 hidden shrink-0 text-xs text-muted-foreground md:inline">
                <span key={tUi("impersonating")}>{tUi("impersonating")}</span></span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-72"
          // Keep focus on the filter field instead of the first menu item on open.
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            filterInputRef.current?.focus()
          }}
        >
          <DropdownMenuLabel><span key={tUi("switchTenant")}>{tUi("switchTenant")}</span></DropdownMenuLabel>
          <div className="px-2 pb-2 pt-1">
            <Input
              ref={filterInputRef}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={tUi("filterTenants")}
              className="h-8"
              // Stop the dropdown's typeahead from swallowing the typed characters,
              // but let navigation/close keys bubble to Radix.
              onKeyDown={(e) => {
                if (!["ArrowDown", "ArrowUp", "Escape", "Enter"].includes(e.key)) {
                  e.stopPropagation()
                }
              }}
            />
          </div>
          <DropdownMenuSeparator />

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : filteredTenants.length === 0 ? (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground"><span key={tUi("noTenantsFound")}>{tUi("noTenantsFound")}</span></div>
          ) : (
            <div className="max-h-[500px] overflow-y-auto">
              {filteredTenants.map((tenant) => (
                <DropdownMenuItem
                  key={tenant.id}
                  onClick={() => impersonate(tenant.id)}
                  disabled={switching || tenant.id === currentTenantId}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{tenant.name}</span>
                    {tenant.id === currentTenantId && <Check className="h-4 w-4 text-primary" />}
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {isImpersonating && (
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          onClick={stopImpersonation}
          disabled={switching}
          title={tUi("stopImpersonation")}
          aria-label={tUi("stopImpersonation")}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
